/* DivisionStripBar — slim secondary bar that sits directly below the
 * division header and surfaces the OTHER 2 divisions + About ZHELTA as
 * small horizontal pill cards. Matches the user's screenshot style:
 *
 *    [THREE DIVISIONS, ONE ZHELTA]  [icon] ZHELTA Systems →   [icon] ZHELTA Academy →   [icon] About ZHELTA →
 *    └── gold eyebrow              └── other 2 divisions shown as thin outlined pills, right-arrow per row
 *
 * Background is the same warm cream (#faf6ee) used in the header top
 * utility bar so the strip reads as a natural continuation of the header.
 *
 * Usage: <DivisionStripBar current="systems" />
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Cpu, GraduationCap, Building2, ShoppingBag } from 'lucide-react';

const DIVISIONS = {
  systems:  { to: '/systems',  label: 'ZHELTA Systems',  tagline: 'Enterprise IT & deployment', Icon: Cpu,           iconBg: 'bg-blue-100',   iconColor: 'text-blue-600' },
  services: { to: '/services', label: 'ZHELTA Services', tagline: 'Verified hardware & gear',    Icon: ShoppingBag,   iconBg: 'bg-zinc-200',  iconColor: 'text-zinc-800' },
  academy:  { to: '/academy',  label: 'ZHELTA Academy',  tagline: 'Practical IT education',      Icon: GraduationCap, iconBg: 'bg-purple-100', iconColor: 'text-purple-600' },
  about:    { to: '/about',    label: 'About ZHELTA',    tagline: 'Who we are',                   Icon: Building2,     iconBg: 'bg-zinc-200',  iconColor: 'text-zinc-800' },
};

export default function DivisionStripBar({ current }) {
  // Show the other 2 divisions + About ZHELTA, always in the same order
  // so the bar reads consistently across pages.
  const order = ['systems', 'services', 'academy'].filter((k) => k !== current).concat(['about']);

  return (
    <div
      className="bg-white border-b border-zinc-100"
      data-testid={`division-strip-${current}`}
    >
      <div className="zh-container flex items-center gap-3 lg:gap-4 py-2.5 flex-wrap">
        <span className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-zinc-700 shrink-0">
          Three divisions, one ZHELTA
        </span>
        <div className="flex items-center gap-2 lg:gap-3 flex-wrap">
          {order.map((key) => {
            const d = DIVISIONS[key];
            const Icon = d.Icon;
            return (
              <Link
                key={key}
                to={d.to}
                data-testid={`division-strip-pill-${key}`}
                className="group inline-flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full border border-gray-200 bg-white hover:border-[#c9a227] hover:shadow-[0_2px_10px_-4px_rgba(201,162,39,0.3)] transition-all"
              >
                <span className={`w-6 h-6 rounded-full ${d.iconBg} ${d.iconColor} grid place-items-center`}>
                  <Icon className="w-3 h-3" />
                </span>
                <span className="leading-tight">
                  <span className="block text-[12px] font-bold text-gray-900">{d.label}</span>
                  <span className="block text-[10px] text-gray-500">{d.tagline}</span>
                </span>
                <ArrowRight className="w-3 h-3 text-gray-400 group-hover:text-zinc-700 transition-colors" />
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
